import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AllPostDataType } from './compos/AllPosts/MyFeed';
import { AllReportDataType } from './compos/AllReports/MyReports';
import { profilePicUrl } from './config';

interface PostMetadata {
	likeCount?: number;
	isLiked?: boolean;
	commentCount?: number;
	isClosed?: boolean;
}

interface DataContextType {
	posts: Record<number, AllPostDataType[]>;
	setPosts: (topic: number, posts: AllPostDataType[]) => void;
	reports: AllReportDataType[];
	setReports: (reports: AllReportDataType[]) => void;
	clearData: () => void;
	profilePicCache: Record<string, string>;
	lastRefreshTime: number;
	postMetadata: Record<number, PostMetadata>;
	updatePostMetadata: (id: number, data: PostMetadata) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: React.ReactNode }) {
	const [posts, setPosts] = useState<Record<number, AllPostDataType[]>>({});
	const [reports, setReports] = useState<AllReportDataType[]>([]);
	const [profilePicCache, setProfilePicCache] = useState<Record<string, string>>({});
	const [lastRefreshTime, setLastRefreshTime] = useState<number>(Date.now());
	const [postMetadata, setPostMetadata] = useState<Record<number, PostMetadata>>({});

	// Load cached data on mount
	useEffect(() => {
		// Check if this is a real page refresh by comparing navigation type
		const isRealPageRefresh =
			(window.performance && window.performance.navigation && window.performance.navigation.type === 1) ||
			!sessionStorage.getItem('lastRefreshTime');

		if (isRealPageRefresh) {
			// Clear existing data on real refresh
			setPosts({});
			setReports([]);
			setPostMetadata({});
			const currentTime = Date.now();
			setLastRefreshTime(currentTime);
			sessionStorage.setItem('lastRefreshTime', currentTime.toString());
		} else {
			const storedRefreshTime = sessionStorage.getItem('lastRefreshTime');
			if (storedRefreshTime) {
				setLastRefreshTime(parseInt(storedRefreshTime));
			}
		}

		// Load cached data. NOTE: reports are deliberately NOT restored from
		// localStorage — see the save effect below.
		const cachedData = localStorage.getItem('appData');
		if (cachedData && !isRealPageRefresh) {
			// A corrupted/partial write (quota hit, crash mid-write) would make
			// JSON.parse throw on every load and brick the app for that user until
			// they manually clear storage. Guard it: on parse failure, drop the bad
			// blob and start clean.
			try {
				const { posts: cachedPosts, profilePics, postMetadata: cachedMetadata } = JSON.parse(cachedData);
				setPosts(cachedPosts || {});
				setProfilePicCache(profilePics || {});
				setPostMetadata(cachedMetadata || {});
			} catch {
				localStorage.removeItem('appData');
			}
		}
	}, []);

	// Persist cache. Posts (public news) and profile-pic URLs are fine to keep in
	// localStorage, but REPORTS are private (visible only to the reporter and the
	// authorities in the chain) and can be sensitive. Writing them to localStorage
	// left them readable on disk on a shared/public computer long after the user
	// walked away. Reports now live only in memory + the existing per-tab
	// sessionStorage caches (which clear on tab close), never in persistent storage.
	useEffect(() => {
		// Wrapped: localStorage.setItem throws on quota exhaustion (a big feed +
		// profile-pic cache can approach the 5 MB cap). A throw here would blow up
		// the render; instead drop the cache and move on — it's an optimization,
		// not a source of truth.
		try {
			localStorage.setItem(
				'appData',
				JSON.stringify({
					posts,
					profilePics: profilePicCache,
					postMetadata,
				})
			);
		} catch {
			try {
				localStorage.removeItem('appData');
			} catch {
				/* ignore */
			}
		}
	}, [posts, profilePicCache, postMetadata]);

	const updatePostMetadata = useCallback((id: number, data: PostMetadata) => {
		setPostMetadata((prev) => ({
			...prev,
			[id]: { ...prev[id], ...data },
		}));
	}, []);

	const setPostsForTopic = useCallback(
		(topic: number, newPosts: AllPostDataType[]) => {
			setPosts((prev) => ({ ...prev, [topic]: newPosts }));

			// Cache profile pictures and metadata
			newPosts.forEach((post) => {
				if (post.profilepicURL && !profilePicCache[post.profilepicURL]) {
					setProfilePicCache((prev) => ({
						...prev,
						[post.profilepicURL]: profilePicUrl(post.profilepicURL),
					}));
				}
				updatePostMetadata(post.id, {
					likeCount: post.likeCount,
					isLiked: post.isLiked === 1,
					commentCount: post.commentCount,
				});
			});
		},
		[profilePicCache, updatePostMetadata]
	);

	const setReportsWithCache = useCallback(
		(newReports: AllReportDataType[]) => {
			setReports(newReports);

			// Cache profile pictures and metadata
			newReports.forEach((report) => {
				if (report.profilepicURL && !profilePicCache[report.profilepicURL]) {
					setProfilePicCache((prev) => ({
						...prev,
						[report.profilepicURL]: profilePicUrl(report.profilepicURL),
					}));
				}
				updatePostMetadata(report.id, {
					commentCount: report.commentCount,
					isClosed: report.isClosed,
				});
			});
		},
		[profilePicCache, updatePostMetadata]
	);

	const clearData = useCallback(() => {
		setPosts({});
		setReports([]);
		setPostMetadata({});
		localStorage.removeItem('appData');

		// Clear all sessionStorage feed caches so that a different user logging in
		// never sees stale data from the previous session.
		['received', 'sent'].forEach((tab) => {
			sessionStorage.removeItem(`reportsFeedState_${tab}`);
			sessionStorage.removeItem(`reportsScrollPos_${tab}`);
		});
		sessionStorage.removeItem('newsFeedState');
		sessionStorage.removeItem('newsScrollPos');
		sessionStorage.removeItem('exploreFeedState');
		sessionStorage.removeItem('exploreScrollPos');
		sessionStorage.removeItem('globalImageCache');
		sessionStorage.removeItem('lastRefreshTime');
		// Admin panel state (authority/user) — also user-specific
		sessionStorage.removeItem('userAdminState');
		sessionStorage.removeItem('authorityAdminState');
		sessionStorage.removeItem('adminActiveTab');
	}, []);

	return (
		<DataContext.Provider
			value={{
				posts,
				setPosts: setPostsForTopic,
				reports,
				setReports: setReportsWithCache,
				clearData,
				profilePicCache,
				lastRefreshTime,
				postMetadata,
				updatePostMetadata,
			}}
		>
			{children}
		</DataContext.Provider>
	);
}

export function useData() {
	const context = useContext(DataContext);
	if (context === undefined) {
		throw new Error('useData must be used within a DataProvider');
	}
	return context;
}
